import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

import { PlayerProvider } from './context/PlayerContext';
import NowPlayingBar from './components/NowPlayingBar';
import ProtectedRoute from './components/ProtectedRoute';

import Header from './components/Header';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import HowItWorksSection from './components/HowItWorksSection';
import PricingSection from './components/PricingSection';
import Footer from './components/Footer';
import Signup from './components/Signup';
import Signin from './components/Signin';
import CallToActionSection from './components/CallToActionSection';
import CreateRadioStep1 from './components/CreateRadioStep1';
import CreateRadioStep2 from './components/CreateRadioStep2';
import CreateRadioStep3 from './components/CreateRadioStep3';
import RadioPage from './pages/RadioPage';

import Favorites from './pages/Favorites';
import History from './pages/History';
import Playlists from './pages/Playlists';
import Profile from './pages/Profile';
import MyRadios from './pages/MyRadios';
import RadioStudio from './pages/RadioStudio';
import PublicRadios from './pages/PublicRadios';
import PublicRadio from './pages/PublicRadio';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import ForgotPassword from './pages/ForgotPassword';

// Page d’accueil. Gère aussi le défilement vers #features / #pricing lorsque
// l'utilisateur arrive depuis un autre écran via un lien de type /#section.
function Home() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace('#', '');
    // Laisse le DOM se peindre avant de défiler.
    const raf = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [hash]);

  return (
    <div className="bg-white text-gray-900 scroll-smooth">
      <Header />
      <HeroSection />
      <FeaturesSection />

      <div className="bg-gradient-to-b from-[#f5f3ff] to-white">
        <HowItWorksSection />
        <PricingSection />
        <CallToActionSection />
      </div>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <PlayerProvider>
      <Router>
        <Routes>
          {/* Page d’accueil */}
          <Route path="/" element={<Home />} />

          {/* Auth */}
          <Route path="/signup" element={<Signup />} />
          <Route path="/signin" element={<><Header /><Signin /><Footer /></>} />

          {/* Flux email (vérification & mot de passe) — publics */}
          <Route path="/verify-email" element={<><Header /><VerifyEmail /><Footer /></>} />
          <Route path="/forgot-password" element={<><Header /><ForgotPassword /><Footer /></>} />
          <Route path="/reset-password" element={<><Header /><ResetPassword /><Footer /></>} />

          {/* Création & gestion de radios */}
          <Route path="/creer-radio/step1" element={<ProtectedRoute><CreateRadioStep1 /></ProtectedRoute>} />
          <Route path="/creer-radio/step2" element={<ProtectedRoute><CreateRadioStep2 /></ProtectedRoute>} />
          <Route path="/creer-radio/step3" element={<ProtectedRoute><CreateRadioStep3 /></ProtectedRoute>} />
          <Route path="/mes-radios" element={<ProtectedRoute><MyRadios /></ProtectedRoute>} />
          <Route path="/radio/:id" element={<ProtectedRoute><RadioStudio /></ProtectedRoute>} />
          <Route path="/radios" element={<PublicRadios />} />
          <Route path="/r/:slug" element={<PublicRadio />} />
          <Route path="/radio" element={<RadioPage />} />

          {/* App — écoute (routes dormantes, non liées dans la navigation) */}
          <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
          <Route path="/playlists" element={<ProtectedRoute><Playlists /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        </Routes>

        <NowPlayingBar />
      </Router>
    </PlayerProvider>
  );
}

export default App;
